import type { Locale } from '@iac/contracts';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { DonutChart } from './DonutChart';
import { Panel } from './Panel';

const sample: Dictionary<{
  disk: string;
  used: string;
  free: string;
  database: string;
  core: string;
  calories: string;
  brain: string;
  budget: string;
  spent: string;
  planned: string;
  left: string;
  overBudget: string;
  over: string;
}> = {
  pl: {
    disk: 'Zajętość dysku',
    used: 'Zajęte',
    free: 'Wolne',
    database: 'Rozmiar bazy',
    core: 'Platforma',
    calories: 'Kalorie',
    brain: 'Mózg',
    budget: 'Budżet miesiąca',
    spent: 'Wydane',
    planned: 'Zaplanowane',
    left: 'Pozostało',
    overBudget: 'Przekroczony budżet',
    over: 'Ponad budżet',
  },
  en: {
    disk: 'Disk usage',
    used: 'Used',
    free: 'Free',
    database: 'Database size',
    core: 'Platform',
    calories: 'Calories',
    brain: 'Brain',
    budget: 'Monthly budget',
    spent: 'Spent',
    planned: 'Planned',
    left: 'Left',
    overBudget: 'Over budget',
    over: 'Over budget',
  },
};

function Chart({
  locale,
  label,
  slices,
  unit,
  center,
}: {
  locale: Locale;
  label: string;
  slices: { label: string; value: number; tone?: 'danger' }[];
  unit: string;
  center?: string;
}) {
  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return (
    <Panel as="div">
      <DonutChart
        label={label}
        slices={slices}
        center={center}
        formatValue={(value) => `${number.format(value)} ${unit}`}
      />
    </Panel>
  );
}

export default {
  kind: 'component',
  title: { pl: 'Wykres pierścieniowy', en: 'Donut chart' },
  description: {
    pl: "Części całości jako pierścień z legendą — każdy wycinek z wartością, także zerowy. Kolory idą według pozycji, więc ważniejszy wycinek podaj pierwszy („Zajęte” przed „Wolne”). Wycinek z `tone: 'danger'` jest czerwony — na przekroczenie limitu — i nie przesuwa kolorów pozostałych. `center` trafia do środka pierścienia; formatowanie wartości należy do wołającego.",
    en: 'Parts of a whole as a ring with a legend — every slice with its value, zero ones included. Tones go by position, so put the slice that matters first ("Used" before "Free"). A slice with `tone: \'danger\'` is red — for going past a limit — and leaves the others’ tones as they were. `center` sits in the hole; formatting values is the caller’s.',
  },
  variants: [
    {
      name: { pl: 'Dysk', en: 'Disk' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <Chart
            locale={locale}
            label={t.disk}
            unit="GB"
            center="62%"
            slices={[
              { label: t.used, value: 41.2 },
              { label: t.free, value: 25.1 },
            ]}
          />
        );
      },
    },
    {
      name: { pl: 'Kilka wycinków', en: 'Several slices' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <Chart
            locale={locale}
            label={t.database}
            unit="MB"
            slices={[
              { label: t.core, value: 12.4 },
              { label: t.calories, value: 8.1 },
              { label: t.brain, value: 3.6 },
            ]}
          />
        );
      },
    },
    {
      name: { pl: 'Wycinek zerowy', en: 'A zero slice' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <Chart
            locale={locale}
            label={t.budget}
            unit="zł"
            center="100%"
            slices={[
              { label: t.spent, value: 3200 },
              { label: t.planned, value: 0 },
              { label: t.left, value: 0 },
            ]}
          />
        );
      },
    },
    {
      name: { pl: 'Przekroczony budżet', en: 'Over budget' },
      render: (locale) => {
        const t = sample[locale];
        return (
          <Chart
            locale={locale}
            label={t.overBudget}
            unit="zł"
            center="115%"
            slices={[
              { label: t.budget, value: 3000 },
              { label: t.over, value: 450, tone: 'danger' },
            ]}
          />
        );
      },
    },
  ],
} satisfies Story;
